import type {
  Clubhouse_LeagueRoundV2Dto,
  Clubhouse_RoundSetting,
  Clubhouse_PatchLeagueV2Round,
} from "../../types/leagues";
import { isRoundEditable, resolveCourseId } from "../helpers";

/**
 * Round settings that "apply to later rounds" copies: the playing conditions,
 * scoring & handicap, and automation the operator wants consistent across a
 * season. Deliberately EXCLUDES the schedule (play window), course, and holes
 * (`selectedHoles`). Tees belong to the league, so they are not round settings
 * and are not copied here.
 */
export const PROPAGATED_SETTING_KEYS = [
  // Conditions
  "pinType",
  "stimpSetting",
  "windSpeed",
  "windDirection",
  "skyConditions",
  "groundConditions",
  "timeConditions",
  // Handicap. No longer surfaced in the UI — the backend owns handicapping —
  // but still sent, and still propagated between rounds, because these were
  // optional on the way in and the backend most likely does not default them.
  // Dropping them would leave the round with no values rather than the ones it
  // has today, so they stay until the API is confirmed to default them itself.
  "handicapMode",
  "handicapPercentage",
  "courseRatingSlope",
  // Automation
  "autoConcede",
  "autoContinue",
  "autoDrop",
  "autoGimmes",
  "gimmeDistance",
  "mulligans",
] as const satisfies readonly (keyof Clubhouse_RoundSetting)[];

/**
 * Merge the propagated fields from `source` onto a copy of `target`'s settings.
 * Starting from the target's own settings preserves everything not being copied
 * (course holes stay on the target round). Tees are league settings, so they
 * are not part of this copy.
 */
export function mergePropagatedSettings(
  target: Clubhouse_RoundSetting | undefined,
  source: Clubhouse_RoundSetting,
): Clubhouse_RoundSetting {
  const merged: Clubhouse_RoundSetting = { ...target };
  for (const key of PROPAGATED_SETTING_KEYS) {
    // Assign through a widened record: each key's value type is preserved by the
    // shared Clubhouse_RoundSetting shape, but TS can't prove key-by-key here.
    (merged as Record<string, unknown>)[key] = source[key];
  }
  return merged;
}

/**
 * Rounds that a "save to future rounds" action should target: rounds after the
 * one just edited that are still editable (Scheduled and not past their window).
 * Finalized/in-progress rounds are skipped — their settings are locked.
 *
 * A flex league enforces no window, so passing `isFlex` keeps a stale window
 * from quietly excluding rounds the operator can still edit.
 */
export function eligibleLaterRounds(
  rounds: Clubhouse_LeagueRoundV2Dto[],
  currentRoundNumber: number,
  isFlex = false,
): Clubhouse_LeagueRoundV2Dto[] {
  return rounds.filter(
    (r) =>
      r.roundNumber != null &&
      r.roundNumber > currentRoundNumber &&
      r.id != null &&
      isRoundEditable(r.status, r.playWindowEndUtc, isFlex),
  );
}

/**
 * Build the PATCH body that applies `sourceSettings` to a single target round.
 * The target keeps its own play window, course, and holes; only the propagated
 * settings change. Sends the target's own play window and holes back so those
 * never depend on partial-PATCH semantics.
 *
 * Course is the exception: the target's `courseId` is a SluggedId the write
 * contract can't take, so the numeric Course.id is resolved from `courseName`.
 * When that fails the field is omitted, which the API treats as "leave the
 * course unchanged".
 */
export function toApplySettingsPatchBody(
  target: Clubhouse_LeagueRoundV2Dto,
  sourceSettings: Clubhouse_RoundSetting,
  courses: { id: number; displayName: string }[] = [],
): Clubhouse_PatchLeagueV2Round {
  const courseId = resolveCourseId(target.courseName, courses);
  return {
    playWindowStartUtc: target.playWindowStartUtc,
    playWindowEndUtc: target.playWindowEndUtc,
    ...(courseId > 0 ? { courseId } : {}),
    selectedHoles: target.selectedHolesJson,
    roundSettings: mergePropagatedSettings(target.settings, sourceSettings),
  };
}
